import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import Edit from '@mui/icons-material/Edit';
import FlashOn from '@mui/icons-material/FlashOn';
import Download from '@mui/icons-material/Download';
import Upload from '@mui/icons-material/Upload';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import QrCodeIcon from '@mui/icons-material/QrCode';
import LoginIcon from '@mui/icons-material/Login';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import { styled, useTheme } from '@mui/material/styles';
import React, { useEffect, useReducer, useState } from "react";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ScriptModal from "../../components/ScriptModal";
import ScriptTriggerModal from "../../components/ScriptTriggerModal";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import openSocket from "../../services/socket-io";

const reducer = (state, action) => {
  if (action.type === "LOAD") return action.payload;
  if (action.type === "CREATE") return [action.payload, ...state];
  if (action.type === "UPDATE") {
    const idx = state.findIndex(s => s.id === action.payload.id);
    if (idx !== -1) { state[idx] = action.payload; return [...state]; }
    return [action.payload, ...state];
  }
  if (action.type === "DELETE") return state.filter(s => s.id !== action.payload);
  return state;
};

const MainPaper = styled(Paper)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2),
  margin: theme.spacing(1),
  overflowY: "auto",
  ...theme.scrollbarStyles,
  borderRadius: theme.shape.borderRadius,
}));

// ─── Dialog: Compartilhar por Código ──────────────────────────────────────────
const ShareCodeDialog = ({ open, onClose, script }) => {
  const theme = useTheme();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || !script) return;
    setCode(script.shareCode || '');
    setCopied(false);
  }, [open, script]);

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/scripts/${script.id}/share-code`);
      setCode(data.shareCode);
    } catch (err) { toastError(err); }
    finally { setLoading(false); }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{
        backgroundColor: theme.palette.primary.main,
        color: '#fff',
        display: 'flex', alignItems: 'center', gap: 1, py: 1.5
      }}>
        <ShareIcon />
        <Typography variant="subtitle1" fontWeight={700}>
          Compartilhar Script: {script?.name}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Alert severity="info" sx={{ mb: 2.5 }}>
          Gere um código único e envie para qualquer usuário do Chat Recebeu Ai. Ao colar o código, o funil completo será clonado na conta deles.
        </Alert>

        {!code ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <QrCodeIcon sx={{ fontSize: '3rem', color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Nenhum código gerado ainda para este script.
            </Typography>
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <ShareIcon />}
            >
              {loading ? 'Gerando...' : 'Gerar Código de Compartilhamento'}
            </Button>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              Código de compartilhamento:
            </Typography>
            <TextField
              fullWidth
              value={code}
              InputProps={{
                readOnly: true,
                sx: {
                  fontFamily: 'monospace',
                  fontSize: '1.3rem',
                  fontWeight: 700,
                  letterSpacing: 3,
                  color: theme.palette.primary.main,
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={copied ? 'Copiado!' : 'Copiar código'}>
                      <IconButton onClick={handleCopy} color={copied ? 'success' : 'primary'}>
                        {copied ? <CheckCircleIcon /> : <ContentCopyIcon />}
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#f0f7ff',
                  borderRadius: 2,
                }
              }}
            />
            <Alert severity="success" icon={<CheckCircleIcon />}>
              Código ativo! Copie e compartilhe com outros usuários do sistema.
            </Alert>
            <Divider sx={{ my: 2 }} />
            <Button
              variant="outlined"
              size="small"
              onClick={handleGenerate}
              disabled={loading}
              startIcon={<ShareIcon />}
            >
              Gerar novo código (invalida o anterior)
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Dialog: Importar por Código ──────────────────────────────────────────────
const ImportByCodeDialog = ({ open, onClose, onImported }) => {
  const theme = useTheme();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) setCode('');
  }, [open]);

  const handleImport = async () => {
    if (!code.trim()) {
      toast.error('Digite o código de compartilhamento');
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post('/scripts/import-by-code', { code: code.trim() });
      toast.success(`Script "${data.name}" importado com sucesso!`);
      onImported(data);
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{
        backgroundColor: theme.palette.primary.main,
        color: '#fff',
        display: 'flex', alignItems: 'center', gap: 1, py: 1.5
      }}>
        <LoginIcon />
        <Typography variant="subtitle1" fontWeight={700}>Importar Script por Código</Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Cole o código recebido de outro usuário do Chat Recebeu Ai para clonar o funil completo na sua conta.
        </Alert>
        <TextField
          fullWidth
          label="Código de compartilhamento"
          value={code}
          onChange={e => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
          placeholder="Ex: A1B2C3D4E5F6G7H8"
          inputProps={{ maxLength: 16, style: { fontFamily: 'monospace', fontSize: '1.1rem', letterSpacing: 2, fontWeight: 700 } }}
          onKeyDown={e => { if (e.key === 'Enter') handleImport(); }}
          autoFocus
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={loading || !code.trim()}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <LoginIcon />}
        >
          {loading ? 'Importando...' : 'Importar Funil'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Página principal ──────────────────────────────────────────────────────────
const Scripts = () => {
  const [scripts, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [scriptModalOpen, setScriptModalOpen] = useState(false);
  const [triggerModalOpen, setTriggerModalOpen] = useState(false);
  const [selectedScript, setSelectedScript] = useState(null);
  const [deletingScript, setDeletingScript] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [shareScript, setShareScript] = useState(null);
  const [importCodeOpen, setImportCodeOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/scripts").then(({ data }) => {
      dispatch({ type: "LOAD", payload: data });
      setLoading(false);
    }).catch(err => { toastError(err); setLoading(false); });
  }, []);

  useEffect(() => {
    const socket = openSocket();
    socket.on("script", data => {
      if (data.action === "create") dispatch({ type: "CREATE", payload: data.script });
      if (data.action === "update") {
        api.get(`/scripts/${data.scriptId || data.script?.id}`).then(({ data: s }) => {
          dispatch({ type: "UPDATE", payload: s });
        }).catch(() => {});
      }
      if (data.action === "delete") dispatch({ type: "DELETE", payload: data.scriptId });
    });
    return () => socket.disconnect();
  }, []);

  const handleNew = () => { setSelectedScript(null); setScriptModalOpen(true); };
  const handleEdit = (s) => { setSelectedScript(s); setScriptModalOpen(true); };
  const handleDelete = async () => {
    try {
      await api.delete(`/scripts/${deletingScript.id}`);
      toast.success("Script excluído");
    } catch (err) { toastError(err); }
    setDeletingScript(null);
  };

  const handleExportJSON = async (script) => {
    try {
      const { data } = await api.get(`/scripts/${script.id}/export`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `script-${script.name.replace(/\s+/g, "_")}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Script exportado!");
    } catch (err) { toastError(err); }
  };

  const handleImportJSON = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
        const text = await file.text();
        const json = JSON.parse(text);
        const { data } = await api.post("/scripts/import", json);
        dispatch({ type: "CREATE", payload: data });
        toast.success(`Script "${data.name}" importado!`);
      } catch (err) { toastError(err); }
    };
    input.click();
  };

  const typeLabel = (type) => {
    const map = { text: "Texto", audio: "Áudio", image: "Imagem", video: "Vídeo", file: "Arquivo", label: "Etiqueta" };
    return map[type] || type;
  };

  const typeColor = (type) => {
    const map = { text: "default", audio: "success", image: "info", video: "warning", file: "secondary", label: "error" };
    return map[type] || "default";
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={`Excluir script "${deletingScript?.name}"?`}
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={handleDelete}
      >
        Todas as mensagens e gatilhos deste script serão excluídos.
      </ConfirmationModal>

      <ScriptModal
        open={scriptModalOpen}
        onClose={() => setScriptModalOpen(false)}
        script={selectedScript}
        onSaved={(s) => {
          if (selectedScript) dispatch({ type: "UPDATE", payload: s });
          else dispatch({ type: "CREATE", payload: s });
        }}
      />

      <ScriptTriggerModal
        open={triggerModalOpen}
        onClose={() => setTriggerModalOpen(false)}
        scripts={scripts}
      />

      <ShareCodeDialog
        open={!!shareScript}
        onClose={() => setShareScript(null)}
        script={shareScript}
      />

      <ImportByCodeDialog
        open={importCodeOpen}
        onClose={() => setImportCodeOpen(false)}
        onImported={(s) => dispatch({ type: "CREATE", payload: s })}
      />

      <MainHeader>
        <Title>Speed Modes</Title>
        <MainHeaderButtonsWrapper>
          <Tooltip title="Importar funil por código de compartilhamento">
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => setImportCodeOpen(true)}
              startIcon={<LoginIcon />}
              size="small"
            >
              Importar por Código
            </Button>
          </Tooltip>
          <Tooltip title="Importar script via arquivo JSON">
            <Button variant="outlined" onClick={handleImportJSON} startIcon={<Upload />} size="small">
              Importar JSON
            </Button>
          </Tooltip>
          <Tooltip title="Gerenciar Gatilhos">
            <Button variant="outlined" color="warning" onClick={() => setTriggerModalOpen(true)} startIcon={<FlashOn />} size="small">
              Gatilhos
            </Button>
          </Tooltip>
          <Button variant="contained" color="primary" onClick={handleNew} startIcon={<AddCircleOutline />} size="small">
            Novo Speed Mode
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <MainPaper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Descrição</TableCell>
              <TableCell align="center">Mensagens</TableCell>
              <TableCell align="center">Tipos</TableCell>
              <TableCell align="center">Gatilhos</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} align="center"><CircularProgress size={24} sx={{ my: 2 }} /></TableCell></TableRow>
            ) : scripts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Box sx={{ py: 4 }}>
                    <Typography color="textSecondary" gutterBottom>Nenhum script criado ainda</Typography>
                    <Button variant="contained" onClick={handleNew} startIcon={<AddCircleOutline />}>
                      Criar primeiro Speed Mode
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ) : scripts.map(script => (
              <TableRow key={script.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <strong>{script.name}</strong>
                    {script.shareCode && (
                      <Tooltip title={`Código ativo: ${script.shareCode}`}>
                        <Chip
                          label={script.shareCode}
                          size="small"
                          color="primary"
                          variant="outlined"
                          icon={<ShareIcon style={{ fontSize: 12 }} />}
                          sx={{ fontFamily: 'monospace', fontSize: '0.65rem', height: 18, cursor: 'pointer' }}
                          onClick={() => setShareScript(script)}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ color: "text.secondary", fontSize: "0.82rem" }}>
                  {script.description || "—"}
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center", alignItems: "center" }}>
                    <Tooltip title="Etapas do script">
                      <Chip label={script.messages?.length || 0} size="small" />
                    </Tooltip>
                    {(script.quickAnswers?.length > 0) && (
                      <Tooltip title="Respostas rápidas vinculadas">
                        <Chip label={"+" + script.quickAnswers.length + " R"} size="small" color="success" variant="outlined" />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", justifyContent: "center" }}>
                    {[...new Set((script.messages || []).map(m => m.type))].map(t => (
                      <Chip key={t} label={typeLabel(t)} size="small" color={typeColor(t)} variant="outlined" />
                    ))}
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={script.triggers?.length || 0}
                    size="small"
                    color={script.triggers?.length ? "warning" : "default"}
                    icon={<FlashOn style={{ fontSize: 14 }} />}
                  />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Editar script">
                    <IconButton size="small" onClick={() => handleEdit(script)}>
                      <Edit color="info" fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Compartilhar por código">
                    <IconButton size="small" onClick={() => setShareScript(script)}>
                      <ShareIcon color="primary" fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Exportar JSON">
                    <IconButton size="small" onClick={() => handleExportJSON(script)}>
                      <Download fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <IconButton size="small" onClick={() => { setDeletingScript(script); setConfirmOpen(true); }}>
                      <DeleteOutline color="error" fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </MainPaper>
    </MainContainer>
  );
};

export default Scripts;
